"use client";

import React, { useState, useMemo } from "react";
import {
  ExplainableText,
  AnimationMode,
  EasingMode,
  VisualVariant,
  parseExplainableTokens,
  TokenNode,
} from "./ExplainableText";
import {
  Sparkles,
  Type,
  Code2,
  Maximize2,
  Minimize2,
  BookOpen,
  Cpu,
  Clock,
  CheckCircle2,
  Copy,
  Check,
  ChevronRight,
  Printer,
  Zap,
  Activity,
  Bot,
  Layers,
  Gauge,
  SlidersHorizontal,
} from "lucide-react";

interface Preset {
  id: string;
  name: string;
  icon: React.ReactNode;
  category: string;
  text: string;
}

const PRESETS_DE: Preset[] = [
  {
    id: "3d-printer",
    name: "3D-Drucker (Verschachtelt)",
    icon: <Printer className="h-4 w-4 text-rose-500" />,
    category: "Hardware & Fertigung",
    text: "Der Anycubic Kobra 3 basiert auf dem [FDM-Verfahren](Fused Deposition Modeling: Schmelzschichtverfahren mit thermoplastischem Filament) und überzeugt im Home-Office durch sein [leises Arbeitsgeräusch](in Akustik-Tests max. 38 dB(A), was dem Pegel von leisem Flüstern in einer Bibliothek entspricht) sowie eine [hohe Druckgeschwindigkeit](bis zu [600 mm/s](handelsübliche Drucker mit 50 mm/s gelten heute als langsam – 600 mm/s ist State of the Art) bei [20.000 mm/s² Beschleunigung](ermöglicht durch CoreXY-Kinematik und leichte Druckköpfe)). Dank [automatischer Kalibrierung](Vibrationskompensation per Beschleunigungssensor) gelingen Bauteile ab [0,08 mm Schichthöhe](präzise Detailauflösung für Funktionsprototypen) fehlerfrei.",
  },
  {
    id: "ai-summary",
    name: "KI-Zusammenfassung",
    icon: <Bot className="h-4 w-4 text-purple-500" />,
    category: "LLM & Adaptive Berichte",
    text: "Die nächtlichen System-Audits waren [durchweg positiv](alle 64 E2E-Tests bestanden und die API-Latenz sank um [28 %](von 195 ms auf 140 ms im P95-Lasttest)), während der Speicherverbrauch [stabil blieb](Peak-Heap lag bei 48 MB ohne Memory-Leaks). Nur ein [Build-Warnhinweis](veraltete Dependency in '@auth/core' erfordert zeitnahes Update) erfordert Aufmerksamkeit.",
  },
  {
    id: "frontend-arch",
    name: "Frontend Architecture",
    icon: <Zap className="h-4 w-4 text-amber-500" />,
    category: "Web Engineering",
    text: "Durch den Einsatz von [React Server Components](RSC rendern ohne Client-JavaScript auf dem Server) und [Streaming SSR](HTML-Häppchen schrittweise vor dem Hydrieren an den Browser senden) sinkt die [INP-Latenz](Interaction to Next Paint: P90-Reaktionszeit der UI unter [200 ms](Google stuft Reaktionszeiten unter 200 ms als gut und über 500 ms als kritisch ein)) drastisch, während [ISR](Incremental Static Regeneration: statische HTML-Seiten im Hintergrund asynchron neu bauen) für weltweite [Sub-50ms TTFB](Server-Antwortzeit an Edge-Standorten) sorgt.",
  },
  {
    id: "medical",
    name: "Medizinischer Leitfaden",
    icon: <Activity className="h-4 w-4 text-emerald-500" />,
    category: "Gesundheit & Medizin",
    text: "Zur Behandlung von [arterieller Hypertonie](dauerhafter Gefäßblutdruck über [140/90 mmHg](systolischer Druck über 140 mmHg oder diastolischer Druck über 90 mmHg gemessen in Ruhe)) empfiehlt sich neben [ACE-Hemmern](Medikamente zur Entspannung und Erweiterung der Blutgefäße) eine [salzarme Ernährung](unter [5 g Speisesalz pro Tag](entspricht ca. einem gestrichenen Teelöffel gemäß WHO-Leitlinie)) zur wirksamen Vorbeugung von [Schlaganfällen](akute Durchblutungsstörung des Gehirns mit Sauerstoffmangel).",
  },
];

const PRESETS_EN: Preset[] = [
  {
    id: "3d-printer",
    name: "3D Printer (Nested)",
    icon: <Printer className="h-4 w-4 text-rose-500" />,
    category: "Hardware & Manufacturing",
    text: "The Anycubic Kobra 3 is engineered around [FDM technology](Fused Deposition Modeling: Additive manufacturing using melted thermoplastic filament) and excels in home workspaces through its [quiet operation](acoustic lab tests below 38 dB(A), matching a whisper in a quiet library) and [high print velocity](up to [600 mm/s](conventional 50 mm/s printers are now outdated – 600 mm/s is state of the art) with [20,000 mm/s² acceleration](unlocked by CoreXY kinematics and ultra-light toolheads)). With [active vibration compensation](real-time accelerometer calibration), prints achieve crisp quality down to [0.08 mm layer height](precision resolution for functional prototypes).",
  },
  {
    id: "ai-summary",
    name: "AI Summary",
    icon: <Bot className="h-4 w-4 text-purple-500" />,
    category: "LLM & Adaptive Reports",
    text: "Nightly automated test suites were [overwhelmingly positive](all 64 E2E test suites passed and P95 API latency dropped by [28%](from 195 ms down to 140 ms under simulated load)), while memory footprint [remained completely stable](peak heap stayed at 48 MB with zero leaks detected). Only one [build warning](outdated dependency in '@auth/core' requires timely patch) needs attention.",
  },
  {
    id: "frontend-arch",
    name: "Frontend Architecture",
    icon: <Zap className="h-4 w-4 text-amber-500" />,
    category: "Web Engineering",
    text: "By adopting [React Server Components](RSC render without client-side JavaScript bundle on the server) and [Streaming SSR](delivering progressive HTML chunks to the browser before hydration), [INP latency](Interaction to Next Paint: P90 user interaction latency maintained below [200 ms](Google rates responsiveness under 200 ms as good and over 500 ms as poor)) plummets drastically, while [ISR](Incremental Static Regeneration: background revalidation of cached static pages) guarantees global [sub-50ms TTFB](time to first byte across distributed edge networks).",
  },
  {
    id: "medical",
    name: "Clinical Guidelines",
    icon: <Activity className="h-4 w-4 text-emerald-500" />,
    category: "Health & Medicine",
    text: "When managing [essential hypertension](persistent blood pressure exceeding [140/90 mmHg](systolic blood pressure over 140 mmHg or diastolic pressure over 90 mmHg at rest)), guidelines recommend pairing [ACE inhibitors](therapies that relax and dilate blood vessels) with a [low-sodium diet](less than [5 g of table salt per day](approximately one level teaspoon per WHO guidelines)) to effectively mitigate the risk of [cerebrovascular strokes](acute disruption of cerebral blood circulation and oxygen supply).",
  },
];

const PRESETS = PRESETS_DE;

/**
 * Flattens all token IDs recursively for global expand/collapse.
 */
function collectAllTokenIds(nodes: TokenNode[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.type === "explainable" && node.id) {
      ids.push(node.id);
      if (node.children) {
        ids.push(...collectAllTokenIds(node.children));
      }
    }
  }
  return ids;
}

interface PlaygroundProps {
  locale?: "de" | "en";
}

export default function ExplainableTextPlayground({ locale = "de" }: PlaygroundProps) {
  const presets = locale === "en" ? PRESETS_EN : PRESETS_DE;
  const isEn = locale === "en";

  const defaultCustomText = isEn
    ? "Our engineering squad leverages [Trunk-Based Development](continuous merging into a single trunk branch with short-lived [feature branches](branch lifetime under 24 hours to prevent merge debt)) coupled with [Feature Flags](conditional code toggles to progressively expose features to users) to double our [deployment frequency](cadence of verified production releases)."
    : "Unser Team nutzt [Trunk-Based Git](Entwicklung auf einem einzigen Haupt-Branch mit kurzlebigen [Feature-Branches](Branch-Lebensdauer unter 24 Stunden für minimale Merge-Konflikte)) in Kombination mit [Feature Flags](Schalter im Code, um Funktionen für Nutzer gezielt freizuschalten), um die [Deployment-Frequenz](Häufigkeit erfolgreicher Releases in Produktion) zu verdoppeln.";

  const [activeTab, setActiveTab] = useState<"preview" | "sandbox" | "code">("preview");
  const [selectedPresetId, setSelectedPresetId] = useState<string>("3d-printer");
  const [visualVariant, setVisualVariant] = useState<VisualVariant>("subtle");
  const [animationMode, setAnimationMode] = useState<AnimationMode>("typewriter");
  const [easingMode, setEasingMode] = useState<EasingMode>("accelerating");
  const [speedMs, setSpeedMs] = useState<number>(14);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [customText, setCustomText] = useState<string>(defaultCustomText);
  const [copiedCode, setCopiedCode] = useState(false);

  const activePreset = useMemo(() => {
    return presets.find((p) => p.id === selectedPresetId) || presets[0];
  }, [presets, selectedPresetId]);

  const currentText = activeTab === "sandbox" ? customText : activePreset.text;

  // Parsed node structure
  const parsedNodes = useMemo(() => parseExplainableTokens(currentText), [currentText]);
  const allTokenIds = useMemo(() => collectAllTokenIds(parsedNodes), [parsedNodes]);

  const totalTokensCount = allTokenIds.length;
  const activeExpandedCount = expandedIds.filter((id) => allTokenIds.includes(id)).length;

  const handleToggleId = (id: string, next: boolean) => {
    setExpandedIds((prev) => {
      if (next) {
        return prev.includes(id) ? prev : [...prev, id];
      } else {
        // Also collapse nested children if parent is collapsed
        return prev.filter((i) => i !== id && !i.startsWith(`${id}-c`));
      }
    });
  };

  const handleExpandAll = () => {
    setExpandedIds(allTokenIds);
  };

  const handleCollapseAll = () => {
    setExpandedIds([]);
  };

  const handleCopyCode = () => {
    const snippet = `// ExplainableText.tsx
import React, { useState, useEffect, useRef } from "react";
import { ChevronRight } from "lucide-react";

export function ExplainableInline({
  term,
  explanation,
  variant = "subtle", // "subtle" | "badge"
}: {
  term: string;
  explanation: string;
  variant?: "subtle" | "badge";
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayed, setDisplayed] = useState("");
  const fullText = \` (\${explanation})\`;

  useEffect(() => {
    if (!isExpanded) {
      setDisplayed("");
      return;
    }
    let cur = 0;
    // Anrampung / Accelerating easing
    const scheduleNext = () => {
      cur++;
      setDisplayed(fullText.slice(0, cur));
      if (cur < fullText.length) {
        const progress = cur / fullText.length;
        const delay = Math.max(4, Math.round(32 - Math.pow(progress, 0.5) * 28));
        setTimeout(scheduleNext, delay);
      }
    };
    scheduleNext();
  }, [isExpanded, fullText]);

  return (
    <span className="inline">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className={
          variant === "subtle"
            ? "inline-flex items-center gap-1 border-b-2 border-rose-500 font-semibold text-neutral-900 dark:text-white hover:text-rose-600 transition-colors cursor-pointer mx-0.5 px-0.5"
            : "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 mx-0.5 font-semibold text-rose-700 bg-rose-500/10 border border-rose-500/25 transition-all cursor-pointer"
        }
      >
        <span>{term}</span>
        <ChevronRight className={\`h-3 w-3 text-rose-500 transition-transform \${isExpanded ? "rotate-180" : ""}\`} />
      </button>
      {isExpanded && (
        <span className="inline text-neutral-750 dark:text-neutral-250 font-normal">
          {displayed}
        </span>
      )}
    </span>
  );
}`;
    navigator.clipboard.writeText(snippet);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <div className="flex flex-col gap-6 select-none min-h-[520px]">
      {/* Top Bar: Tabs & Actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-neutral-200/80 pb-4 dark:border-neutral-800">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-1.5 rounded-xl border border-neutral-200 bg-neutral-100/80 p-1 dark:border-neutral-800 dark:bg-neutral-900">
          <button
            type="button"
            onClick={() => setActiveTab("preview")}
            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
              activeTab === "preview"
                ? "bg-white text-rose-700 shadow-xs dark:bg-neutral-800 dark:text-rose-300"
                : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
            }`}
          >
            <BookOpen className="h-3.5 w-3.5" />
            <span>{isEn ? "Presets & Nesting" : "Presets & Verschachtelung"}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("sandbox")}
            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
              activeTab === "sandbox"
                ? "bg-white text-rose-700 shadow-xs dark:bg-neutral-800 dark:text-rose-300"
                : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
            }`}
          >
            <Type className="h-3.5 w-3.5" />
            <span>{isEn ? "Custom Text (Sandbox)" : "Eigener Text (Sandbox)"}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("code")}
            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
              activeTab === "code"
                ? "bg-white text-rose-700 shadow-xs dark:bg-neutral-800 dark:text-rose-300"
                : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
            }`}
          >
            <Code2 className="h-3.5 w-3.5" />
            <span>React Code</span>
          </button>
        </div>

        {/* Global Expand / Collapse Actions with robust Dark Mode styling */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExpandAll}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700 transition-all cursor-pointer shadow-2xs"
            title={isEn ? "Expand all terms (including nested levels)" : "Alle Begriffe (auch verschachtelte) öffnen"}
          >
            <Maximize2 className="h-3 w-3 text-rose-500" />
            <span>{isEn ? "Expand all" : "Alle öffnen"}</span>
          </button>

          <button
            type="button"
            onClick={handleCollapseAll}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700 transition-all cursor-pointer shadow-2xs"
            title={isEn ? "Collapse all explanations" : "Alle Erklärungen schließen"}
          >
            <Minimize2 className="h-3 w-3 text-neutral-400" />
            <span>{isEn ? "Collapse all" : "Alle schließen"}</span>
          </button>
        </div>
      </div>

      {/* Preset Cards */}
      {activeTab === "preview" && (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          {presets.map((p) => {
            const isSelected = p.id === selectedPresetId;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setSelectedPresetId(p.id);
                  setExpandedIds([]);
                }}
                className={`flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-all cursor-pointer ${
                  isSelected
                    ? "border-rose-500/50 bg-rose-50/60 ring-2 ring-rose-500/20 dark:border-rose-500/40 dark:bg-rose-950/20 dark:ring-rose-500/20"
                    : "border-neutral-200 bg-white hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-900/60 dark:hover:border-neutral-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  {p.icon}
                  <span className="font-heading text-xs font-bold text-neutral-900 dark:text-white">
                    {p.name}
                  </span>
                </div>
                <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
                  {p.category}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Studio Workbench Controls: Styling, Animation & Easing */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-neutral-200/80 bg-neutral-50/60 p-3.5 dark:border-neutral-800 dark:bg-neutral-900/40">
        {/* Style Variant: Subtle (Standard) vs Badge */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 flex items-center gap-1">
            <SlidersHorizontal className="h-3 w-3 text-rose-500" />
            <span>{isEn ? "Style:" : "Darstellung:"}</span>
          </span>
          <div className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-white p-0.5 dark:border-neutral-700 dark:bg-neutral-800">
            <button
              type="button"
              onClick={() => setVisualVariant("subtle")}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                visualVariant === "subtle"
                  ? "bg-rose-500 text-white shadow-2xs font-semibold"
                  : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              {isEn ? "Subtle (Underline & Arrow)" : "Dezent (Linie & Pfeil)"}
            </button>
            <button
              type="button"
              onClick={() => setVisualVariant("badge")}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                visualVariant === "badge"
                  ? "bg-rose-500 text-white shadow-2xs font-semibold"
                  : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              {isEn ? "Badge (Pill)" : "Badge (Pille)"}
            </button>
          </div>
        </div>

        {/* Animation Mode Selector */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-neutral-600 dark:text-neutral-400">
            {isEn ? "Mode:" : "Modus:"}
          </span>
          <div className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-white p-0.5 dark:border-neutral-700 dark:bg-neutral-800">
            <button
              type="button"
              onClick={() => setAnimationMode("typewriter")}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                animationMode === "typewriter"
                  ? "bg-rose-500 text-white shadow-2xs font-semibold"
                  : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              ✍️ Typewriter
            </button>
            <button
              type="button"
              onClick={() => setAnimationMode("slide")}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                animationMode === "slide"
                  ? "bg-rose-500 text-white shadow-2xs font-semibold"
                  : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              🌊 Fluid Slide
            </button>
            <button
              type="button"
              onClick={() => setAnimationMode("instant")}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                animationMode === "instant"
                  ? "bg-rose-500 text-white shadow-2xs font-semibold"
                  : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              {isEn ? "⚡ Instant (a11y)" : "⚡ Sofort (a11y)"}
            </button>
          </div>
        </div>

        {/* Easing Selector (when Typewriter is active) */}
        {animationMode === "typewriter" && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 flex items-center gap-1">
              <Gauge className="h-3 w-3 text-amber-500" />
              <span>Timing:</span>
            </span>
            <div className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-white p-0.5 dark:border-neutral-700 dark:bg-neutral-800">
              <button
                type="button"
                onClick={() => setEasingMode("accelerating")}
                className={`rounded-md px-2 py-0.5 text-xs font-medium transition-all cursor-pointer ${
                  easingMode === "accelerating"
                    ? "bg-rose-500 text-white shadow-2xs font-semibold"
                    : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                }`}
                title={isEn ? "Starts smooth and accelerates rapidly (ideal for long sentences)" : "Startet sanft und wird rasant schneller (ideal für lange Sätze)"}
              >
                {isEn ? "🚀 Accelerating" : "🚀 Anrampung"}
              </button>
              <button
                type="button"
                onClick={() => setEasingMode("smooth")}
                className={`rounded-md px-2 py-0.5 text-xs font-medium transition-all cursor-pointer ${
                  easingMode === "smooth"
                    ? "bg-rose-500 text-white shadow-2xs font-semibold"
                    : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                }`}
                title={isEn ? "Gentle start, faster middle, smooth settle" : "Sanfter Start, schneller Mittelteil, sanfter Auslauf"}
              >
                {isEn ? "S-Curve" : "S-Kurve"}
              </button>
              <button
                type="button"
                onClick={() => setEasingMode("linear")}
                className={`rounded-md px-2 py-0.5 text-xs font-medium transition-all cursor-pointer ${
                  easingMode === "linear"
                    ? "bg-rose-500 text-white shadow-2xs font-semibold"
                    : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                }`}
                title={isEn ? "Constant velocity" : "Gleichmäßige Geschwindigkeit"}
              >
                {isEn ? "Constant" : "Konstant"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Reading Preview */}
      {activeTab === "preview" && (
        <div className="rounded-2xl border border-neutral-200/90 bg-white p-6 md:p-8 shadow-xs dark:border-neutral-800 dark:bg-neutral-900/90">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-400 dark:text-neutral-500">
            <span className="font-mono uppercase tracking-wider flex items-center gap-1.5">
              <span>{activePreset.name}</span>
              <span className="inline-flex items-center rounded-full bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold text-rose-600 dark:text-rose-400">
                {isEn ? "Nested levels" : "Mehrstufig verschachtelt"}
              </span>
            </span>
            <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
              {isEn
                ? "Click underlined terms ▸ (also within opened expansions!)"
                : "Klicke auf unterstrichene Begriffe ▸ (auch in geöffneten Texten!)"}
            </span>
          </div>

          {/* Reading Container */}
          <div className="text-base sm:text-lg leading-relaxed text-neutral-800 dark:text-neutral-200">
            <ExplainableText
              text={activePreset.text}
              mode={animationMode}
              easing={easingMode}
              variant={visualVariant}
              speedMs={speedMs}
              expandedIds={expandedIds}
              onToggleId={handleToggleId}
            />
          </div>
        </div>
      )}

      {/* Sandbox Tab */}
      {activeTab === "sandbox" && (
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl border border-neutral-200/90 bg-white p-5 shadow-xs dark:border-neutral-800 dark:bg-neutral-900">
            <label
              htmlFor="sandbox-input"
              className="mb-2 block font-heading text-xs font-bold text-neutral-700 dark:text-neutral-300"
            >
              {isEn ? (
                <>Markdown source with nested syntax: <span className="font-mono text-rose-600 dark:text-rose-400">[term](explanation with [subterm](details))</span></>
              ) : (
                <>Quelltext mit verschachtelter Markdown-Syntax: <span className="font-mono text-rose-600 dark:text-rose-400">[Begriff](Erklärung mit [Unterbegriff](Details))</span></>
              )}
            </label>
            <textarea
              id="sandbox-input"
              rows={4}
              value={customText}
              onChange={(e) => {
                setCustomText(e.target.value);
                setExpandedIds([]);
              }}
              placeholder={isEn ? "Write a sentence with [term](explanation) ..." : "Schreibe einen Satz mit [Wort](Erklärung) ..."}
              className="w-full rounded-xl border border-neutral-200 bg-neutral-50/70 p-3.5 font-sans text-sm text-neutral-900 focus:border-rose-500 focus:outline-hidden dark:border-neutral-700 dark:bg-neutral-800 dark:text-white"
            />
            <div className="mt-2.5 flex items-center justify-between">
              <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
                {isEn
                  ? "Tip: Arbitrarily nestable – inner expressions are parsed automatically."
                  : "Tipp: Beliebig tief verschachtelbar – inneres Markup wird automatisch erkannt."}
              </span>
              <button
                type="button"
                onClick={() => {
                  setCustomText(
                    isEn
                      ? "In 3D printing, the extruder achieves a [high print velocity](up to [600 mm/s](conventional printers print at only 50 mm/s) at [20,000 mm/s² acceleration](CoreXY drive)), producing functional prototypes in record time."
                      : "Im 3D-Druck erreicht der Extruder eine [hohe Druckgeschwindigkeit](bis zu [600 mm/s](herkömmliche Drucker drucken mit nur 50 mm/s) bei [20.000 mm/s² Beschleunigung](CoreXY-Antrieb)), wodurch Prototypen in Rekordzeit fertiggestellt werden."
                  );
                  setExpandedIds([]);
                }}
                className="text-xs font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400 cursor-pointer"
              >
                {isEn ? "Insert nested example" : "Verschachteltes Beispiel einfügen"}
              </button>
            </div>
          </div>

          {/* Sandbox Live Result */}
          <div className="rounded-2xl border border-neutral-200/90 bg-white p-6 md:p-8 shadow-xs dark:border-neutral-800 dark:bg-neutral-900">
            <div className="mb-3 font-mono text-xs uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              {isEn ? "Live Result:" : "Live-Ergebnis:"}
            </div>
            <div className="text-base sm:text-lg leading-relaxed text-neutral-800 dark:text-neutral-200">
              <ExplainableText
                text={customText}
                mode={animationMode}
                easing={easingMode}
                variant={visualVariant}
                speedMs={speedMs}
                expandedIds={expandedIds}
                onToggleId={handleToggleId}
              />
            </div>
          </div>
        </div>
      )}

      {/* Code Tab */}
      {activeTab === "code" && (
        <div className="relative overflow-hidden rounded-2xl border border-neutral-800 bg-zinc-950 p-4 font-mono text-xs text-neutral-300">
          <div className="mb-3 flex items-center justify-between border-zinc-800 border-b pb-2">
            <span className="font-semibold text-neutral-400">ExplainableText.tsx ({isEn ? "with easing & chevron" : "mit Anrampung & Pfeil"})</span>
            <button
              type="button"
              onClick={handleCopyCode}
              className="inline-flex items-center gap-1.5 rounded-md bg-zinc-800 px-2.5 py-1 text-[11px] font-medium text-zinc-200 hover:bg-zinc-700 transition-colors cursor-pointer"
            >
              {copiedCode ? (
                <>
                  <Check className="h-3 w-3 text-emerald-400" />
                  <span>{isEn ? "Copied!" : "Kopiert!"}</span>
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  <span>{isEn ? "Copy code" : "Code kopieren"}</span>
                </>
              )}
            </button>
          </div>
          <pre className="overflow-x-auto text-zinc-300 leading-relaxed max-h-[360px]">
{`// Dezent & Beschleunigend
export function ExplainableInline({
  term,
  explanation,
  variant = "subtle", // "subtle" (Linie & Pfeil) oder "badge"
}: {
  term: string;
  explanation: string;
  variant?: "subtle" | "badge";
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayed, setDisplayed] = useState("");
  const fullText = \` (\${explanation})\`;

  useEffect(() => {
    if (!isExpanded) { setDisplayed(""); return; }
    let cur = 0;
    // Anrampung / Accelerating Easing (32ms -> 4ms)
    const scheduleNext = () => {
      cur++;
      setDisplayed(fullText.slice(0, cur));
      if (cur < fullText.length) {
        const progress = cur / fullText.length;
        const delay = Math.max(4, Math.round(32 - Math.pow(progress, 0.5) * 28));
        setTimeout(scheduleNext, delay);
      }
    };
    scheduleNext();
  }, [isExpanded, fullText]);

  return (
    <span className="inline">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className={
          variant === "subtle"
            ? "inline-flex items-center gap-1 border-b-2 border-rose-500 font-semibold text-neutral-900 dark:text-white hover:text-rose-600 transition-colors cursor-pointer mx-0.5 px-0.5"
            : "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 mx-0.5 font-semibold text-rose-700 bg-rose-500/10 border border-rose-500/25 transition-all cursor-pointer"
        }
      >
        <span>{term}</span>
        <ChevronRight className={\`h-3 w-3 text-rose-500 transition-transform \${isExpanded ? "rotate-180" : ""}\`} />
      </button>
      {isExpanded && (
        <span className="inline text-neutral-750 dark:text-neutral-250 font-normal">
          {displayed}
        </span>
      )}
    </span>
  );
}`}
          </pre>
        </div>
      )}

      {/* Realtime Metrics Bar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 pt-2">
        <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-900/50">
          <div className="flex items-center gap-1.5 text-neutral-500 dark:text-neutral-400 text-[11px]">
            <Layers className="h-3 w-3 text-rose-500" />
            <span>Erkundungstiefe</span>
          </div>
          <div className="mt-1 font-heading text-lg font-bold text-neutral-900 dark:text-white">
            {activeExpandedCount} / {totalTokensCount} aktiv
          </div>
          <div className="text-[10px] text-neutral-400">
            {totalTokensCount > 0 ? `${Math.round((activeExpandedCount / totalTokensCount) * 100)}% geöffnet` : "0%"}
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-900/50">
          <div className="flex items-center gap-1.5 text-neutral-500 dark:text-neutral-400 text-[11px]">
            <Gauge className="h-3 w-3 text-amber-500" />
            <span>Streaming-Easing</span>
          </div>
          <div className="mt-1 font-heading text-base font-bold text-neutral-900 dark:text-white">
            {easingMode === "accelerating" ? "Anrampung (32➔4ms)" : easingMode === "smooth" ? "S-Kurve" : "Konstant"}
          </div>
          <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
            Keine Wartezeit bei langen Texten
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-900/50">
          <div className="flex items-center gap-1.5 text-neutral-500 dark:text-neutral-400 text-[11px]">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            <span>Ergonomie</span>
          </div>
          <div className="mt-1 font-heading text-lg font-bold text-emerald-600 dark:text-emerald-400">
            Zero Popups
          </div>
          <div className="text-[10px] text-neutral-400">
            Pfeil nach rechts ▸ rotiert nach unten
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-900/50">
          <div className="flex items-center gap-1.5 text-neutral-500 dark:text-neutral-400 text-[11px]">
            <Cpu className="h-3 w-3 text-sky-500" />
            <span>INP / Re-Render</span>
          </div>
          <div className="mt-1 font-heading text-lg font-bold text-neutral-900 dark:text-white">
            &lt; 16 ms
          </div>
          <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
            Atomarer State / 60 FPS
          </div>
        </div>
      </div>
    </div>
  );
}
